import { Injectable, inject } from '@angular/core';
import { WatchQueryFetchPolicy, ObservableQuery } from '@apollo/client';
import { ToastService } from '@ih/app/client/shared/services';
import {
  FindAllProjectMembersResponse,
  ProjectMember,
} from '@ih/app/shared/apis/interfaces';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { produce } from 'immer';
import { catchError, take, tap } from 'rxjs';
import {
  FindAllProjectMembers,
  ResetUserProjectsList,
  SetProjects,
} from '../actions';
import { FindAllProjectsError, FindAllProjectsSuccess } from '../effects';

export interface UserProjectsStateModel {
  projectMember: ProjectMember[];
  total: number;
}

@State<UserProjectsStateModel>({
  name: 'userProjects',
  defaults: {
    projectMember: [],
    total: 0,
  },
})
@Injectable()
export class UserProjectsState {
  private readonly apollo = inject(Apollo);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);

  @Selector()
  public static getState(state: UserProjectsStateModel) {
    return state;
  }

  @Selector()
  public static projectMembers(state: UserProjectsStateModel): ProjectMember[] {
    return state.projectMember;
  }

  @Selector()
  public static total(state: UserProjectsStateModel): number {
    return state.total;
  }

  @Action(FindAllProjectMembers)
  public findAllProjectsForUser(
    ctx: StateContext<UserProjectsStateModel>,
    { searchTerm, pageNumber }: FindAllProjectMembers,
  ) {
    const userId = this.store.selectSnapshot<string>(
      (state) => state.user.user.id,
    );

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindProjectMembers(
        $userId: String!
        $searchTerm: String
        $pageNumber: Int
      ) {
        findAllProjectsForUser(
          request: {
            where: { userId: $userId, projectDeleted: false }
            searchTerm: $searchTerm
            limit: 15
            page: $pageNumber
          }
        ) {
          projectMembers {
            id
            favourite
            project {
              id
              name
              slug
              deleted
              userId
              description
              photoURL
              user {
                displayName
                username
              }
              groupLabel {
                name
                slug
                description
              }
              organisationLabel {
                name
                slug
                description
              }
            }
          }
          total
        }
      }
    `;

    const postsQuery = this.apollo.watchQuery<{
      findAllProjectsForUser: FindAllProjectMembersResponse;
    }>({
      query,
      variables: {
        userId,
        searchTerm,
        pageNumber,
      },
      fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
    });
    postsQuery.refetch();
    return postsQuery.valueChanges.pipe(
      take(1),
      tap(
        (
          result: ObservableQuery.Result<{
            findAllProjectsForUser: FindAllProjectMembersResponse;
          }>,
        ) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          ctx.dispatch(new FindAllProjectsSuccess(result));
        },
      ),
      catchError((error) => ctx.dispatch(new FindAllProjectsError(error))),
    );
  }

  @Action(FindAllProjectsSuccess)
  public findAllProjectsSuccess(
    ctx: StateContext<UserProjectsStateModel>,
    { result }: FindAllProjectsSuccess,
  ) {
    const projectMembers: ProjectMember[] = (
      (result?.data?.findAllProjectsForUser!.projectMembers as
        | ProjectMember[]
        | undefined) || []
    )
      .filter(function (projectMember) {
        if (projectMember && projectMember.project) {
          return true;
        }
        return false;
      }, [])
      .sort((a, b) => {
        if (a.project == null || b.project == null) return 0;
        return (a.project.name ?? '').localeCompare(b.project.name ?? '');
      });

    ctx.dispatch(new SetProjects(projectMembers));

    ctx.patchState({
      total: result?.data?.findAllProjectsForUser!.total,
    });
  }

  @Action(FindAllProjectsError)
  public findAllProjectsError(
    ctx: StateContext<UserProjectsStateModel>,
    { error }: FindAllProjectsError,
  ) {
    this.toastService.showError('Failed to fetch projects');
    console.error('Failed to fetch projects: ', error.message);
  }

  @Action(SetProjects)
  public setProjects(
    ctx: StateContext<UserProjectsStateModel>,
    { projectMember }: SetProjects,
  ) {
    ctx.setState(
      produce((draft) => {
        draft.projectMember = projectMember;
      }),
    );
  }

  @Action(ResetUserProjectsList)
  public resetUserProjectsList(ctx: StateContext<UserProjectsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.projectMember = [];
      }),
    );
  }
}
